// Footnote pin targets (code-plan/qfu/plan-tableCellFootnotes.md). A note is pinned to a target id: a
// FootNoteableFixedLabel's or a rich-text block's (SlateStaticData) footnoteId, or a table cell's (its value's
// footnoteId). Notes number in reading order, the order of the targets in the template.

const BLOCKS = ['FootNoteableFixedLabel', 'SlateStaticData'];

const TABLES = ['SuppresableRowTable', 'SimpleTable'];

/**
 * Every pin target in the template, in reading order, shaped as the Note Tags sidebar reads them. A pin id that
 * alternative variants share (the two fees intros, the mix headings) is listed once, at its first place: the
 * sidebar numbers a note by its target's position here, so a repeat would skip a number.
 */
export function pinTargets(template) {
	const targets = [];
	(template?.pages || []).forEach(page => (page.content || []).forEach(element => {
		if (BLOCKS.includes(element.className)) {
			if (element.configuration && element.configuration.footnoteId) targets.push(element);
			return;
		}
		if (!TABLES.includes(element.className)) return;
		const config = element.configuration || {};
		const cells = [config.headerRow || [], ...(config.rows || []).map(row => row.values || []), ...(config.cells || [])];
		cells.forEach(row => row.forEach(cell => {
			if (cell && cell.footnoteId) targets.push({ className: 'TableCell', configuration: { footnoteId: cell.footnoteId } });
		}));
	}));
	const seen = new Set();
	return targets.filter(target => !seen.has(target.configuration.footnoteId) && seen.add(target.configuration.footnoteId));
}

/**
 * Rich text (a Slate JSON string, or its nodes) with the superscript run added at the end of its last line of
 * text, as the PDF's FootnoteMarks does: a paragraph, or the last item of a closing list. Unchanged without a
 * superscript or text to mark; a string stays a string.
 */
export function withSuperscriptRun(content, superscript) {
	if (!superscript || !content) return content;
	let nodes;
	try {
		nodes = typeof content === 'string' ? JSON.parse(content) : JSON.parse(JSON.stringify(content));
	} catch (e) {
		// plain text: one paragraph with the mark, as the PDF's FootnoteMarks makes it
		return JSON.stringify([{ type: 'text', children: [{ text: content }, { text: String(superscript), superscript: true }] }]);
	}
	const lastTextBlock = node => {
		if (Array.isArray(node)) {
			for (let i = node.length - 1; i >= 0; i--) {
				const found = lastTextBlock(node[i]);
				if (found) return found;
			}
			return null;
		}
		const children = node && node.children;
		if (!Array.isArray(children) || !children.length) return null;
		const last = children[children.length - 1];
		if (typeof last.text === 'string' && !last.children) return node;
		return lastTextBlock(children);
	};
	const block = lastTextBlock(nodes);
	if (!block) return content;
	block.children.push({ text: String(superscript), superscript: true });
	return typeof content === 'string' ? JSON.stringify(nodes) : nodes;
}

// Rich text as a copy of its nodes; plain text read as the PDF's FootnoteMarks reads a plain cell: soft breaks and
// non-breaking spaces dropped, "&amp;" unescaped, one line per "<br>"
function richNodes(content) {
	try {
		return typeof content === 'string' ? JSON.parse(content) : JSON.parse(JSON.stringify(content));
	} catch (e) {
		const plain = String(content).replace(/[\r\n]+/g, '').replace(/\u00a0/g, ' ').replace(/&amp;/g, '&');
		return plain.split('<br>').map(line => ({ type: 'text', children: [{ text: line }] }));
	}
}

// The first text run holding {@code after}: the run list it is in, its index there and the block holding it
function findRun(nodes, after, block = null) {
	if (!Array.isArray(nodes)) return null;
	for (let i = 0; i < nodes.length; i++) {
		const node = nodes[i];
		if (typeof node.text === 'string' && !node.children) {
			if (node.text.includes(after)) return { runs: nodes, index: i, block };
			continue;
		}
		const found = findRun(node.children, after, node);
		if (found) return found;
	}
	return null;
}

// the mark takes the words' colour, as the PDF carries a run's colour on to the next
const markRun = (superscript, run) => ({ text: String(superscript), superscript: true, ...(run.color ? { color: run.color } : {}) });

/**
 * Rich text with the superscript run straight after the first {@code after} in it (a cell's footnoteAfter: the
 * template words the mark follows, such as a panel's heading), as the PDF's FootnoteMarks does; at the end, as
 * withSuperscriptRun, when the words are not there. A string stays a string.
 */
export function withSuperscriptRunAfter(content, after, superscript) {
	if (!superscript || !content || !after) return withSuperscriptRun(content, superscript);
	const nodes = richNodes(content);
	const found = findRun(nodes, after);
	if (!found) return withSuperscriptRun(content, superscript);
	const { runs, index } = found;
	const run = runs[index];
	const end = run.text.indexOf(after) + after.length;
	const rest = run.text.substring(end);
	runs.splice(index, 1, { ...run, text: run.text.substring(0, end) }, markRun(superscript, run), ...(rest ? [{ ...run, text: rest }] : []));
	return typeof content === 'string' ? JSON.stringify(nodes) : nodes;
}

/**
 * Rich text with the first {@code after} in it split out as a run of its own carrying {@code pinTarget: pinId}, so
 * only those words are the cell's pin target (runColourAddin draws the run as the .footnoteTarget), as a
 * FootNoteableFixedLabel's footnoteAfter words are; with a superscript, its mark follows the words, outside the
 * target. Null when the words are not there, or are not in a paragraph (runColourAddin draws paragraphs only), so
 * the whole cell stays the target. A string stays a string.
 */
export function withPinTargetRun(content, after, pinId, superscript) {
	if (!content || !after || !pinId) return null;
	const nodes = richNodes(content);
	const found = findRun(nodes, after);
	if (!found || !found.block || found.block.type !== 'text') return null;
	const { runs, index } = found;
	const run = runs[index];
	const at = run.text.indexOf(after);
	const end = at + after.length;
	runs.splice(index, 1,
		...(at > 0 ? [{ ...run, text: run.text.substring(0, at) }] : []),
		{ ...run, text: after, pinTarget: pinId },
		...(superscript ? [markRun(superscript, run)] : []),
		...(end < run.text.length ? [{ ...run, text: run.text.substring(end) }] : []));
	return typeof content === 'string' ? JSON.stringify(nodes) : nodes;
}

/** footnoteId -> superscript, from the document's FOOTNOTES data (a JSON string). */
export function superscriptsByPin(footnotesJson) {
	try {
		const notes = JSON.parse(footnotesJson || '[]');
		return Array.isArray(notes) ? Object.fromEntries(notes.map(note => [note.footnoteId, note.superscript])) : {};
	} catch (e) {
		return {};
	}
}

/** Two note ids name the same note, compared as text as noteKey does (6 and "6"); a missing id names none. */
export function sameNote(a, b) {
	return a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);
}

/**
 * The note a pin belongs to: its offerNoteId, compared as text (the server's FootNotes and renumbering read it as
 * text too, so 6 and "6" are one note everywhere); a pin without one is a note of its own.
 */
function noteKey(pin, i) {
	return pin.offerNoteId === undefined || pin.offerNoteId === null ? `pin:${i}` : `note:${pin.offerNoteId}`;
}

/**
 * The pins numbered as notes (code-plan/qfu/plan-footnoteReuse.md). In reading order, the place of a pin's target
 * in {@code targets} (pinTargets), a note takes the next number at its first pin and its later pins share it, so a
 * note pinned to several places prints one number and one line in the notes. A pin whose target is not in the
 * template goes last, with no number, as before. Without targets the list's own order is the reading order (the
 * stored list is in superscript order), and a pin stored without a number stays unnumbered.
 */
export function numberPins(pins, targets) {
	// no targets yet (the template still loading): keep the saved order rather than unplace every pin
	const order = targets?.length > 0 && Object.fromEntries(targets.map((target, i) => [target.configuration.footnoteId, i]));
	const at = (pin, i) => order ? order[pin.footnoteId] : (pin.superscript === undefined || pin.superscript === null ? undefined : i);
	const placed = (pins || []).map((pin, i) => ({ pin, i, at: at(pin, i) }));
	placed.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity) || a.i - b.i);
	const numbers = new Map();
	return placed.map(({ pin, i, at }) => {
		if (at === undefined) {
			const { superscript, ...unplaced } = pin;
			return unplaced;
		}
		const note = noteKey(pin, i);
		if (!numbers.has(note)) numbers.set(note, numbers.size + 1);
		return { ...pin, superscript: numbers.get(note) };
	});
}

/** The pins as notes, for the Note Tags list: one entry per note, its first pin's fields plus every pin's target id ({@code places}). */
export function pinsByNote(pins) {
	const notes = [];
	const byNote = new Map();
	(pins || []).forEach((pin, i) => {
		const note = noteKey(pin, i);
		if (byNote.has(note)) {
			byNote.get(note).places.push(pin.footnoteId);
			return;
		}
		const entry = { ...pin, places: [pin.footnoteId] };
		byNote.set(note, entry);
		notes.push(entry);
	});
	return notes;
}

const LABEL_LENGTH = 40;

/**
 * A pin place's name for the Note Tags list: the start of the target's own text on the page, as an author sees it
 * (code-plan/qfu/plan-footnoteReuse.md); "Place n" when the target is not on the page. Marks (<sup>) and the
 * editor's embedded <style> text are left out, and blocks are separated by a space.
 */
export function placeLabel(element, index) {
	const parts = [];
	const walk = node => {
		if (node.nodeType === 3) parts.push(node.nodeValue);
		else if (node.nodeType === 1 && !['SUP', 'STYLE', 'SCRIPT'].includes(node.tagName)) {
			if (['P', 'DIV', 'LI', 'BR'].includes(node.tagName)) parts.push(' ');
			node.childNodes.forEach(walk);
		}
	};
	if (element) walk(element);
	const text = parts.join('').replace(/\s+/g, ' ').trim();
	if (!text) return `Place ${index + 1}`;
	return text.length > LABEL_LENGTH ? text.slice(0, LABEL_LENGTH).trim() + '…' : text;
}
